import React from 'react';
import {
  AppBar, Box, Button, ButtonBase, Divider, IconButton, ListItemIcon, Menu, MenuItem, Toolbar, Tooltip, Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import LogoutIcon from '@mui/icons-material/Logout';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import EuroIcon from '@mui/icons-material/Euro';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { UserAvatar } from '../features/planner/common';
import { APP_BAR_HEIGHT } from '../theme';

/** The app's areas. On a phone only the icons show, so the bar never needs a hamburger menu. */
const AREAS = [
  { label: 'Início', path: '/', icon: <DashboardOutlinedIcon fontSize="small" /> },
  { label: 'Casa', path: '/casa', icon: <HomeOutlinedIcon fontSize="small" /> },
  { label: 'Investimentos', path: '/stocks', icon: <ShowChartIcon fontSize="small" /> },
  { label: 'Despesas', path: '/expenses', icon: <EuroIcon fontSize="small" /> },
];

const isActive = (pathname: string, path: string) =>
  path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(path + '/');

const AppTemplate: React.FC = () => {
  const { logout, user, profile } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  // Below md the areas show only their icons, so they get a tooltip
  const compact = useMediaQuery(useTheme().breakpoints.down('md'));

  const go = (path: string) => { setMenuAnchor(null); navigate(path); };
  const handleLogout = () => {
    setMenuAnchor(null);
    logout();
    navigate('/login');
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        color="inherit"
        sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar disableGutters sx={{ minHeight: { xs: APP_BAR_HEIGHT, sm: APP_BAR_HEIGHT }, px: { xs: 1, sm: 2 }, gap: { xs: 0.5, md: 1 } }}>
          <ButtonBase
            onClick={() => navigate('/')}
            aria-label="Página inicial"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRadius: 2, px: 0.5, py: 0.5, mr: { xs: 0.5, md: 2 } }}
          >
            <img src="/logo192.png" alt="" style={{ height: 30, width: 30, objectFit: 'contain' }} />
            <Typography sx={{ fontWeight: 700, fontSize: 17, display: { xs: 'none', lg: 'block' } }} noWrap>Home System</Typography>
          </ButtonBase>

          <Box component="nav" aria-label="Áreas" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, md: 0.5 }, flexGrow: 1, minWidth: 0 }}>
            {AREAS.map(area => {
              const active = isActive(pathname, area.path);
              return (
                <Tooltip key={area.path} title={compact ? area.label : ''}>
                  <Button
                    onClick={() => navigate(area.path)}
                    aria-current={active ? 'page' : undefined}
                    aria-label={area.label}
                    startIcon={area.icon}
                    sx={theme => ({
                      minWidth: 0, px: { xs: 1.25, md: 1.5 }, py: 0.75, borderRadius: 2.5, whiteSpace: 'nowrap',
                      color: active ? 'primary.dark' : 'text.secondary',
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.11) : 'transparent',
                      '&:hover': { bgcolor: active ? alpha(theme.palette.primary.main, 0.14) : 'action.hover' },
                      '& .MuiButton-startIcon': { mr: { xs: 0, md: 0.75 }, ml: 0 },
                    })}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>{area.label}</Box>
                  </Button>
                </Tooltip>
              );
            })}
          </Box>

          {user && (
            <>
              <Tooltip title={user.email || user.name || 'Conta'}>
                <IconButton onClick={e => setMenuAnchor(e.currentTarget)} aria-label="Conta" sx={{ p: 0.5 }}>
                  <UserAvatar name={profile?.name || user.email || ''} photoUrl={profile?.photoUrl} size={34} />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>{profile?.name || user.email}</Typography>
                  {profile?.name && user.email && <Typography variant="caption" color="text.secondary" noWrap component="div">{user.email}</Typography>}
                </Box>
                <Divider />
                <MenuItem onClick={() => go('/casa/conta')}>
                  <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>A minha conta
                </MenuItem>
                <MenuItem onClick={() => go('/mobile-app')}>
                  <ListItemIcon><PhoneAndroidIcon fontSize="small" /></ListItemIcon>App Android
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Sair
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Outlet />
    </>
  );
};

export default AppTemplate;
